import type { BadgeVariant } from '../components/ui';
import type { ActionName, FlagName, PolitiqueTransfert, StatutBillet, StatutCommande } from '../api/types';

/** Guide §8.2 — statut de commande. */
export const STATUT_COMMANDE: Record<StatutCommande, string> = {
  0: 'Normal',
  1: 'Reportée',
  2: 'Annulée',
  3: 'Revente totale',
};

/** Guide §8.3 — politique de transfert. */
export const POLITIQUE_TRANSFERT: Record<PolitiqueTransfert, string> = {
  0: 'Interdit',
  1: 'Autorisé',
  2: 'Obligatoire',
  3: 'Autorisé, 1 à conserver',
  4: 'Obligatoire, 1 à conserver',
};

/** Guide §8.1 — statut de billet. Attention : 2 (en attente) précède 1 (accepté). */
export const STATUT_BILLET: Record<StatutBillet, string> = {
  0: 'Disponible',
  1: 'Transfert accepté',
  2: 'Transfert en attente',
  3: 'En revente',
  4: 'Revendu',
  5: 'Transfert annulé',
  6: 'Annulé',
  7: 'Transfert expiré',
};

export const TYPE_CODE_BARRE: Record<number, string> = {
  0: 'Code128',
  1: 'QR code',
};

/** Flag raised by each action (docs/bo-api.md). Shown as a hint under every button. */
export const ACTION_FLAG: Record<ActionName, FlagName | 'variable'> = {
  deliverBarcodes: 'actionCodeBarre',
  withdrawBarcodes: 'actionCodeBarre',
  setDisplayDate: 'actionCodeBarre',
  setDeferred: 'actionCodeBarre',
  setTransferPolicy: 'action',
  setOrderStatus: 'action',
  setPlacement: 'action',
  setResale: 'action',
  setSold: 'action',
  cancelTicket: 'action',
  resetTicket: 'variable',
  simulateTransferCancelled: 'none',
  simulateTransferExpired: 'none',
  setMaquette: 'variable',
  setFlags: 'variable',
};

/* ------------------------------------------------------ drapeaux en clair (guide §6.1) */

/** Interrupteurs « Rechargement de l'app » : libellé grand public + ligne technique en dessous. */
export const FLAG_SWITCH: Record<'action' | 'actionCodeBarre', { label: string; technical: string }> = {
  action: {
    label: 'Recharger la commande dans l’app à la prochaine synchro',
    technical: 'Lève le drapeau action dans WalletStatutBillet → l’app rappelle WalletDetailCommande.',
  },
  actionCodeBarre: {
    label: 'Recharger la commande et ses QR codes à la prochaine synchro',
    technical: 'Lève le drapeau actionCodeBarre dans WalletStatutBillet → l’app rappelle WalletDetailCommande.',
  },
};

/* ------------------------------------------- options de select, conséquence incluse */

/** Guide §8.3 — ce que chaque politique change dans l'app. */
export const POLITIQUE_TRANSFERT_OPTION: Record<PolitiqueTransfert, string> = {
  0: '0 Interdit — pas de bouton transfert',
  1: '1 Autorisé — bouton simple',
  2: '2 Obligatoire — encadré « Transférez vos billets »',
  3: '3 Autorisé, 1 billet à conserver',
  4: '4 Obligatoire, 1 billet à conserver',
};

/** Guide §8.2 — ce que chaque statut de commande déclenche dans l'app. */
export const STATUT_COMMANDE_OPTION: Record<StatutCommande, string> = {
  0: '0 Normal',
  1: '1 Reportée — dialogue avec ancienne → nouvelle date (nécessite les deux dates)',
  2: '2 Annulée — dialogue bloquant, l’app SUPPRIME la commande (lien à regénérer)',
  3: '3 Revente totale — dialogue bloquant, l’app SUPPRIME la commande (lien à regénérer)',
};

/** A handful of IANA zones, enough for the venues of a recette. */
export const TIMEZONES = [
  'Europe/Paris',
  'Europe/London',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Brussels',
  'Europe/Zurich',
  'Europe/Berlin',
  'Europe/Athens',
  'America/New_York',
  'America/Montreal',
  'Indian/Reunion',
  'America/Guadeloupe',
  'Pacific/Noumea',
  'UTC',
];

export const ETAT_TRANSFERT: Record<string, string> = {
  pending: 'En attente',
  accepted: 'Accepté',
  cancelled: 'Annulé',
  expired: 'Expiré',
};

/* --------------------------------------------------- couleurs des chips (docs/design-tokens.md) */

export const STATUT_COMMANDE_VARIANT: Record<StatutCommande, BadgeVariant> = {
  0: 'outline',
  1: 'jupiter',
  2: 'error',
  3: 'resale',
};

export const STATUT_BILLET_VARIANT: Record<StatutBillet, BadgeVariant> = {
  0: 'success',
  1: 'success',
  2: 'callisto',
  3: 'resale',
  4: 'resale',
  5: 'error',
  6: 'error',
  7: 'jupiter',
};

export const ETAT_TRANSFERT_VARIANT: Record<string, BadgeVariant> = {
  pending: 'callisto',
  accepted: 'success',
  cancelled: 'error',
  expired: 'jupiter',
};

export const FLAG_VARIANT: Record<'action' | 'actionCodeBarre', BadgeVariant> = {
  action: 'primary',
  actionCodeBarre: 'neptune',
};
